import { FC, Fragment, useCallback, useMemo } from 'react'

import { DropdownMenuItemProps } from 'src/components/ui/atoms/DropdownMenuItem/DropdownMenuItem'
import DropdownBoldArrowIcon from 'src/components/ui/atoms/Icons/DropdownBoldArrowIcon'
import Separator from 'src/components/ui/atoms/Separator'
import SidebarMenuItem from 'src/components/ui/atoms/SidebarMenuItem'
import SidebarMenuProductIcon from 'src/components/ui/molecules/SidebarMenuProductIcon/SidebarMenuProductIcon'
import useProductMatchParams from 'src/hooks/useProductMatchParams'
import { cn } from 'src/lib/cn'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import useSidebarStore from 'src/stores/useSidebarStore'
import { AppMenuProductInterface } from 'src/types/AppMenuInterface'
import { isTrueOrUndefinedFn } from 'src/utils/booleanUtils'
import { JPC_FRONTEND_URL } from 'src/utils/config'
import { Product, SubProduct } from 'src/utils/product'
import SidebarProductSubProduct from './SidebarProductSubProduct'

interface SidebarProductsProductProps {
  product: AppMenuProductInterface
}

const SidebarProductsProduct: FC<SidebarProductsProductProps> = ({ product }) => {
  const { isOpen, searchInputFocusedId, toggleCurrentOpenedProduct, currentOpenedProduct } =
    useSidebarStore()

  const isSidebarOpen = isOpen || searchInputFocusedId !== null

  const { name, code, link_code: linkCode, children, has_access } = product

  const hasAccess = isTrueOrUndefinedFn(has_access)

  const { setSelectedDashboard } = useSelectedDashboardStore()

  const routeMatchParam = useProductMatchParams()
  const isActive = routeMatchParam?.params?.product === code

  const subProductParam = routeMatchParam?.params?.subProduct

  const isMenuOpened = currentOpenedProduct === code

  const subProducts = (hasAccess ? children || [] : [])?.filter(
    subProduct => !subProduct.hide_on_menu,
  )

  const hasSubProducts = subProducts.length > 0

  const isProductLink = Boolean(linkCode) && !hasSubProducts

  const isProductLinkActive =
    !hasSubProducts && isActive && linkCode && subProductParam && subProductParam === linkCode

  const isJPCLink = linkCode === SubProduct.JPC

  const handleToggle: DropdownMenuItemProps['onClick'] = useCallback(
    evt => {
      // Open into new window
      if (isJPCLink) {
        evt.stopPropagation()
        evt.preventDefault()
        window.open(JPC_FRONTEND_URL, '_blank')
        return
      }

      if (isProductLink) {
        setSelectedDashboard(null)
      }

      if (hasAccess) {
        toggleCurrentOpenedProduct(code, !isMenuOpened)
      }
    },
    [code, isMenuOpened, toggleCurrentOpenedProduct, hasAccess, isProductLink, isJPCLink],
  )

  const productLinkCode = linkCode ? `/${linkCode}` : ''

  const linkTo = useMemo(() => {
    if (isJPCLink) {
      return undefined
    }
    return hasSubProducts ? undefined : `/${code}${productLinkCode}`
  }, [hasSubProducts, code, productLinkCode, isJPCLink])

  return (
    <>
      <SidebarMenuItem
        label={isSidebarOpen ? name : ''}
        active={isProductLinkActive || isActive}
        icon={<SidebarMenuProductIcon code={code as Product} isActive={isActive} />}
        onClick={handleToggle}
        to={linkTo}
        href={isJPCLink ? JPC_FRONTEND_URL : undefined}
        target={isJPCLink ? '_blank' : undefined}
        rightIcon={
          hasSubProducts && (
            <DropdownBoldArrowIcon
              direction={isMenuOpened ? 'up' : 'down'}
              data-testid={`submenu-chevron-${isMenuOpened ? 'up' : 'down'}`}
              className="text-dark-grey"
            />
          )
        }
        classes={{
          label: isProductLinkActive ? 'text-bay-of-many font-semibold' : undefined,
        }}
        addon={!hasAccess ? `${code}-addon` : undefined}
        className={cn(!isSidebarOpen && 'pl-3 transition-all duration-150')}
        isHovered={!isActive && isMenuOpened && isSidebarOpen ? true : undefined}
        showActiveIndicator={isProductLinkActive || (isSidebarOpen && isMenuOpened ? false : true)}
      />
      {subProducts.map((subProduct, index) => (
        <Fragment key={subProduct.code}>
          <SidebarProductSubProduct
            subProduct={subProduct}
            productCode={code}
            key={subProduct.code}
            isVisible={isMenuOpened && isSidebarOpen}
          />
          {isMenuOpened &&
            isSidebarOpen &&
            index !== subProducts.length - 1 &&
            subProduct.section && (
              <Separator className="bg-loblolly my-2 ml-12 w-[calc(100%-var(--spacing)*16)]" />
            )}
        </Fragment>
      ))}
    </>
  )
}

export default SidebarProductsProduct
